// Hook pro registraci close guardu na navigační vrstvu

import { useEffect, useRef } from 'react';
import { useNavigationContext } from './useNavigationContext';
import type { LayerCloseGuard } from '../core/navigationKernel';

/**
 * Registruje guard pro zavření navigační vrstvy.
 *
 * Guard je async funkce volaná kernelem těsně před uzavřením/nahrazením vrstvy.
 * Vrací `true` = povolení, `false` = zamítnutí (navigace se neprovede).
 *
 * Klíčová vlastnost: guard se NEVOLÁ při otevírání modálů NA vrcholu vrstvy
 * (okno zůstává otevřené). Volá se pouze tehdy, kdy by vrstva skutečně zanikla –
 * přepnutí detailu v list-detail, zavření X tlačítkem, navigace na jiný root.
 *
 * @param layerId - ID vrstvy (z props nebo useAgendaLayer)
 * @param guard   - async guard funkce; `null` = guard není aktivní
 *
 * @example
 * ```tsx
 * useLayerCloseGuard(layerId, async () => {
 *   if (!hasChanges) return true;
 *   return showConfirmDialog();
 * });
 * ```
 */
export function useLayerCloseGuard(
  layerId: string,
  guard: LayerCloseGuard | null | undefined
): void {
  const { kernel } = useNavigationContext();

  // Ref vždy drží nejaktuálnější guard bez nutnosti re-registrace
  const guardRef = useRef(guard);
  guardRef.current = guard;

  useEffect(() => {
    // Stabilní wrapper – kernel ho registruje jednou (při mount)
    const stableGuard: LayerCloseGuard = () => {
      if (!guardRef.current) return Promise.resolve(true);
      return guardRef.current();
    };

    return kernel.registerCloseGuard(layerId, stableGuard);
    // layerId a kernel jsou stabilní po dobu životnosti komponenty
  }, [kernel, layerId]);
}
